import type { BridgeEvent, ShellContextPayload } from "@easy-types";

export type ShellEvent = BridgeEvent | { topic: "easy-shell:context"; payload: ShellContextPayload };
export type ShellEventHandler = (event: ShellEvent) => void;
const EASY_SHELL_EVENT_NAME = "easy-shell:event";

export type EasyAppBridgeApi = {
  isEmbedded: () => boolean;
  navigate: (appId: string) => boolean;
  publish: (topic: string, payload?: Record<string, unknown>) => boolean;
  onShellEvent: (handler: ShellEventHandler) => () => void;
};

declare global {
  interface Window {
    EasyAppBridge?: EasyAppBridgeApi;
  }
}

function isEmbedded() {
  return Boolean(window.parent && window.parent !== window);
}

function navigate(appId: string) {
  if (!isEmbedded()) {
    return false;
  }

  window.parent.postMessage({ type: "easy-shell:navigate", appId }, window.location.origin);
  return true;
}

function publish(topic: string, payload: Record<string, unknown> = {}) {
  const eventPayload: BridgeEvent = {
    topic,
    payload,
    sourceAppId: document.documentElement.dataset.appId || null,
    createdAt: new Date().toISOString(),
  };

  window.dispatchEvent(new CustomEvent<ShellEvent>(EASY_SHELL_EVENT_NAME, { detail: eventPayload }));
  if (isEmbedded()) {
    window.parent.postMessage(
      {
        type: "easy-shell:publish",
        topic,
        payload,
        sourceAppId: eventPayload.sourceAppId,
      },
      window.location.origin
    );
  }
  return true;
}

function onShellEvent(handler: ShellEventHandler) {
  const customEventListener = (event: Event) => {
    const detail = (event as CustomEvent<ShellEvent>).detail;
    if (detail) {
      handler(detail);
    }
  };
  const listener = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) {
      return;
    }

    const message = event.data || {};
    if (message.type === "easy-shell:event") {
      handler(message.event || {});
      return;
    }
    if (message.type === "easy-shell:context") {
      handler({ topic: "easy-shell:context", payload: message as ShellContextPayload });
    }
  };

  window.addEventListener(EASY_SHELL_EVENT_NAME, customEventListener as EventListener);
  window.addEventListener("message", listener);
  return () => {
    window.removeEventListener(EASY_SHELL_EVENT_NAME, customEventListener as EventListener);
    window.removeEventListener("message", listener);
  };
}

export function dispatchEasyShellContext(payload: ShellContextPayload) {
  window.dispatchEvent(
    new CustomEvent<ShellEvent>(EASY_SHELL_EVENT_NAME, {
      detail: { topic: "easy-shell:context", payload },
    })
  );
}

export function installEasyAppBridge(appId: string) {
  document.documentElement.dataset.appId = appId;
  const api: EasyAppBridgeApi = {
    isEmbedded,
    navigate,
    publish,
    onShellEvent,
  };
  window.EasyAppBridge = api;
  return api;
}
